import { BonusSettlementStatus, BonusSettlementType } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { Formik, FormikHelpers } from 'formik';
import { OverviewTableContainer, PoolSpinner } from '@synergy/components';
import { PaloActivityDock } from '@synergy/components-palo';
import { PaloNextThemeProvider } from '@synergy/theme-palo';
import { PoolDocumentTitle } from '@synergy/components-next';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { BonusSettlementSupplierDetailFooter } from './bonus-settlement-supplier-detail-footer';
import { BonusSettlementSupplierDetailFormValues } from './models/bonus-settlement-supplier-detail-form-values';
import { BonusSettlementSupplierDetailOverview } from './bonus-settlement-supplier-detail-overview';
import { BonusSettlementSupplierDetailSidebar } from './components/bonus-settlement-supplier-detail-sidebar';
import { BonusSettlementSupplierDetailTable } from './bonus-settlement-supplier-detail-table';
import { bonusSettlementsSupplierDetailSelector } from './store/bonus-settlements-supplier-detail-selector';
import { fetchBonusSettlementsSupplierOverviewDetails } from '../detail/supplier-overview/store/bonus-settlements-supplier-overview-actions';
import {
    getBonusSettlementsSupplierDetail,
    submitBonusSettlementSupplierDetail,
} from './store/bonus-settlements-supplier-detail-actions';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

export const BonusSettlementSupplierDetail = () => {
    const dispatch = useThunkDispatch();
    const { bonusSettlementId, id: supplierBonusSettlementId } = useParams<{ bonusSettlementId: string; id: string }>();
    const bonusSettlementSupplierDetail = useAppSelector(bonusSettlementsSupplierDetailSelector);

    useEffect(() => {
        if (bonusSettlementId && supplierBonusSettlementId) {
            dispatch(
                getBonusSettlementsSupplierDetail({
                    bonusSettlementId: +bonusSettlementId,
                    supplierBonusSettlementId: +supplierBonusSettlementId,
                })
            );
        }
    }, [bonusSettlementId, dispatch, supplierBonusSettlementId]);

    useEffect(() => {
        // Fetch overview details to get type of bonus settlement, if not already present
        if (bonusSettlementSupplierDetail?.type === undefined && bonusSettlementId && supplierBonusSettlementId) {
            dispatch(fetchBonusSettlementsSupplierOverviewDetails(+bonusSettlementId));
        }
    }, [bonusSettlementId, bonusSettlementSupplierDetail?.type, dispatch, supplierBonusSettlementId]);

    const handleSubmit = (
        values: BonusSettlementSupplierDetailFormValues,
        formikHelpers: FormikHelpers<BonusSettlementSupplierDetailFormValues>
    ) => {
        if (bonusSettlementId && supplierBonusSettlementId) {
            dispatch(
                submitBonusSettlementSupplierDetail({
                    bonusSettlementId: +bonusSettlementId,
                    supplierBonusSettlementId: +supplierBonusSettlementId,
                    data: values,
                })
            );
        }

        formikHelpers.resetForm({ values });
    };

    if (!bonusSettlementSupplierDetail) {
        return (
            <PoolSpinner
                enabled={true}
                sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', pb: 5 }}
            />
        );
    }

    const initialValues: BonusSettlementSupplierDetailFormValues = {
        customerBonusSettlements: bonusSettlementSupplierDetail.customerBonusSettlements.map(settlement => ({
            ...settlement,
            actualBonusValues: [],
        })),
        noteFromSupplier: bonusSettlementSupplierDetail?.noteFromSupplier ?? '',
    };

    if (!bonusSettlementSupplierDetail) {
        return null;
    }

    return (
        <>
            <PoolDocumentTitle
                titleTranslationId={'app.navigation.bonusSettlements'}
                name={bonusSettlementSupplierDetail?.supplierName}
            />
            <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize={true}>
                <>
                    <BonusSettlementSupplierDetailOverview
                        dock={
                            <PaloActivityDock
                                // Reference material next to 299 rows of work:
                                // open by default it would take a third of the
                                // table's height on a laptop.
                                defaultOpen={false}
                                panels={[
                                    {
                                        key: 'details',
                                        label: <FormattedMessage id="app.common.details" />,
                                        content: (
                                            <PaloNextThemeProvider>
                                                <BonusSettlementSupplierDetailSidebar />
                                            </PaloNextThemeProvider>
                                        ),
                                    },
                                ]}
                            />
                        }
                        readOnly={bonusSettlementSupplierDetail.readOnly}
                        year={bonusSettlementSupplierDetail.year}
                        supplierName={bonusSettlementSupplierDetail.supplierName}
                        status={bonusSettlementSupplierDetail.status}
                        bonusConditions={bonusSettlementSupplierDetail.bonusConditions}
                        type={bonusSettlementSupplierDetail.type}
                    >
                        <OverviewTableContainer sx={{ pt: 4 }}>
                            <PaloNextThemeProvider>
                                <BonusSettlementSupplierDetailTable />
                            </PaloNextThemeProvider>
                        </OverviewTableContainer>

                        <PaloNextThemeProvider>
                            <BonusSettlementSupplierDetailFooter
                                readOnly={bonusSettlementSupplierDetail.readOnly}
                                documents={bonusSettlementSupplierDetail.documents}
                                bonusSettlementStatus={
                                    bonusSettlementSupplierDetail.bonusSettlementStatus || BonusSettlementStatus.Draft
                                }
                                bonusSettlementSupplierStatus={bonusSettlementSupplierDetail.status}
                            />
                        </PaloNextThemeProvider>
                    </BonusSettlementSupplierDetailOverview>
                </>
            </Formik>
        </>
    );
};
